import api from 'services/api';
import { REJECT, array, num, object, str, type Received } from 'validation';
import { readResponse } from 'api/shared/readResponse';

// GET /node_definitions — the list read. Backend: routes/node_definitions.js GET via
// modules/generic_functions/node_definitions/getNodeDefinitions.js. The full response is
// `{ nodes, pagination, config, filtersConfig }`.
//
// The list row is a LIGHTER projection than the single-GET record (no parameters / config / child_nodes) and is assembled by
// a different backend query — so `NodeDefinitionRow` is its own type, not `NodeDefinition`. We validate only the identifier
// fields the FE consumes (the async dropdowns) plus `pagination.total_records`; extra list columns the backend sends are
// ignored. The wide row still belongs to `redux/slices/customer_node_definitionsSlice`, which owns the list page — when that
// slice migrates here, this file grows the full row.
//
// Supported query parameters (all optional but `limit`, whose backend default is 10):
//   search      ILIKE '%search%' on the name alone — unlike `/events`, description is not matched
//   name        ILIKE '%name%' on the name too; `search` and `name` are interchangeable here
//   zone_name   exact equals (CSV accepted)
//   node_definition_id  exact equals (CSV accepted) — resolves a stored key back to its label
//   type        exact equals on the node type; the literal 'All' means "no type filter"
//   sortColumn  one of the backend's allow-list; 'name' and 'node_definition_id' are what we use.
//               'name' maps to `LOWER(nd.name), nd.name`, so name order is case-insensitive
//   sortOrder   'ASC' | 'DESC'
//   offset      0-based row offset. `page` is accepted too, but offset takes precedence
export interface NodeDefinitionRow {
  readonly node_definition_id: number;
  // Deliberately stricter than the nullable column: a list row's purpose is to address the record, which is done by
  // `zone_name/name`, and an unnamed definition is unwritable through this API anyway. Reasoning in ./getNodeDefinition.ts.
  readonly name: string;
  readonly zone_name: string;
}

export interface NodeDefinitionsQuery {
  readonly limit: number;
  readonly search?: string;
  readonly name?: string;
  readonly offset?: number;
  readonly zone_name?: string;
  readonly node_definition_id?: number;
  // The node type to restrict to (e.g. 'norscom_python'). Not a union: the types are server-driven
  // (`GET /node_types`), and the backend takes them as free-form strings. The literal 'All' is its
  // sentinel for "no type filter" — the same as omitting the parameter.
  readonly type?: string;
  readonly sortColumn?: 'name' | 'node_definition_id';
  readonly sortOrder?: 'ASC' | 'DESC';
}

export interface NodeDefinitionsListResponse {
  readonly nodes: readonly NodeDefinitionRow[];
  readonly pagination: { readonly total_records: number };
}

const rowSchema = object<NodeDefinitionRow>({
  node_definition_id: num(REJECT),
  name: str(),
  zone_name: str(),
});

const responseSchema = object<NodeDefinitionsListResponse>({
  nodes: array(rowSchema),
  pagination: object<{ readonly total_records: number }>({ total_records: num(REJECT) }),
});

/**
 * GET `/node_definitions`. Answers with the arm; every caller collapses it today. A row whose identity drifted is
 * dropped; a drifted `total_records` fails the call, since nothing can be paged from a count it cannot trust.
 */
export const getNodeDefinitions = async (params: NodeDefinitionsQuery): Promise<Received<NodeDefinitionsListResponse>> => {
  const res = await api.get('/node_definitions', { params });
  return readResponse(res.data, { schema: responseSchema, source: 'getNodeDefinitions' });
};
